import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import classnames from 'classnames';

import { LocationResultsResultGoogleItem } from 'dpl/search/components/LocationResultsList';
import {
  FILTER_POSTAL_CODE,
  FILTER_DISTANCE_IN_MILES,
  FILTER_LATITUDE,
  FILTER_LONGITUDE,
  FILTER_LOCATION_NAME,
  PAGE,
  LOCATION_REDIRECT,
  SORT
} from 'dpl/constants/query_params';
import { pushParams, resetParams } from 'dpl/actions/query_params';
import { addToQuery, removeFromQuery } from 'dpl/util/queryString';
import {
  RESULTS_TYPES,
  SORT_NEAR_YOU
} from 'dpl/puppy_search_results/utils/constants';
import Input from 'dpl/common/design_system/Input';
import withGooglePlaces from 'dpl/decorators/withGooglePlaces';
import { isMobileUA } from 'dpl/shared/utils';

const DEFAULT_DISTANCE = '0';

const LocationResultsList = withGooglePlaces(
  ({ googlePlacesResults, onSelectItem }) => (
    <div className="LocationResultsList">
      {googlePlacesResults.map((place, idx) => (
        <div className="hover-bg-near-white" key={idx}>
          <LocationResultsResultGoogleItem
            place={place}
            onClick={onSelectItem}
          />
        </div>
      ))}
    </div>
  )
);

export default function LocationFilter({
  title,
  inputClassName,
  resultsType,
  isOpen
}) {
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const [isSearching, setIsSearching] = useState(false);
  const { preappliedFilters, rootPath } = useSelector(({ server }) => server);

  const selectedLocation = useSelector(({ queryParams }) => {
    if (queryParams[FILTER_POSTAL_CODE]) {
      return queryParams[FILTER_POSTAL_CODE];
    }

    if (queryParams[FILTER_LOCATION_NAME]) {
      return decodeURIComponent(queryParams[FILTER_LOCATION_NAME]);
    }

    return '';
  });

  const [searchValue, setSearchValue] = useState(selectedLocation);

  useEffect(() => {
    setSearchValue(selectedLocation);
  }, [selectedLocation]);

  useEffect(() => {
    if (!isMobileUA() && isOpen && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isOpen]);

  async function handleLocationSelect(location) {
    const coordinates = await location.getCoordinates();
    const locationName = location.name.replace(/, USA$/, '');

    const params = {
      [FILTER_LOCATION_NAME]: locationName,
      [FILTER_LATITUDE]: coordinates.lat,
      [FILTER_LONGITUDE]: coordinates.lng,
      // We're only passing along distance_in_miles for analytics purposes
      // since postal codes are appended automatically when ip_location_filter
      // so this is the only reliable way to know if location filtering is being done
      [FILTER_DISTANCE_IN_MILES]: DEFAULT_DISTANCE,
      ...(resultsType === RESULTS_TYPES.BREEDERS && { [SORT]: SORT_NEAR_YOU })
    };

    if (preappliedFilters?.stateSlug) {
      const newSearchStr = removeFromQuery(PAGE, window.location.search);

      window.location.href = `${rootPath}/?${LOCATION_REDIRECT}=true&${newSearchStr}#${addToQuery(
        params,
        window.location.hash
      )}`;
    } else {
      dispatch(
        resetParams(
          PAGE,
          FILTER_LATITUDE,
          FILTER_LONGITUDE,
          FILTER_LOCATION_NAME,
          FILTER_POSTAL_CODE
        )
      );
      dispatch(resetParams(FILTER_POSTAL_CODE, { hash: true }));
      dispatch(pushParams(params, { hash: true }));
    }

    setIsSearching(false);
  }

  return (
    <>
      {title && (
        <p className="mb2 font-16 fw-medium md:font-18 md:font-serif">
          {title}
        </p>
      )}
      <p className="font-14 md:font-16 secondary mb6-md mb2">
        We&apos;ll show you puppies from breeders near you.
      </p>
      <p className="mb2 font-12 secondary">Location</p>
      <Input
        name="location"
        type="text"
        ref={inputRef}
        onFocus={() => setIsSearching(true)}
        className={classnames(
          'ba b--light-gray br3 pv2 ph3 mb2',
          inputClassName
        )}
        value={searchValue}
        onChange={e => setSearchValue(e.target.value)}
        placeholder="Enter a city or zipcode"
      />
      {isSearching && (
        <div className="LocationFilter__resultsList overflow-y-auto">
          <LocationResultsList
            query={searchValue}
            onSelectItem={handleLocationSelect}
          />
        </div>
      )}
    </>
  );
}

LocationFilter.propTypes = {
  title: PropTypes.string,
  inputClassName: PropTypes.string,
  resultsType: PropTypes.oneOf(Object.values(RESULTS_TYPES)),
  isOpen: PropTypes.bool
};

LocationFilter.defaultProps = {
  title: null,
  inputClassName: 'w-100 w-50-md',
  resultsType: RESULTS_TYPES.PUPPIES,
  isOpen: false
};
